import type { Attribute, AttributeTreeNode } from 'types/search/attributes'

/**
 * Build hierarchical tree structure from flat list of attributes
 * Splits by "." separator to create parent-child relationships
 *
 * Example:
 * Input: ["user.email", "user.name", "request.method"]
 * Output: {
 *   user: {
 *     email: leaf,
 *     name: leaf
 *   },
 *   request: {
 *     method: leaf
 *   }
 * }
 */

interface TreeNodeMap {
  [key: string]: {
    node: AttributeTreeNode
    children: TreeNodeMap
  }
}

export const buildAttributesTree = (
  attributes: Attribute[],
): AttributeTreeNode[] => {
  const root: TreeNodeMap = {}

  // Build the tree structure
  attributes.forEach((attribute) => {
    // Use 'code' instead of 'name' for splitting
    const parts = attribute.code.split('.')
    let currentLevel = root
    let currentPath = ''

    parts.forEach((part, index) => {
      const isLeaf = index === parts.length - 1
      currentPath = currentPath ? `${currentPath}.${part}` : part

      if (!currentLevel[part]) {
        // For leaf nodes, use the 'name' from attribute, for parent nodes use the part
        const displayTitle = isLeaf ? attribute.name : part

        currentLevel[part] = {
          node: {
            key: currentPath,
            title: displayTitle,
            path: currentPath,
            isLeaf,
            children: [],
          },
          children: {},
        }
      }

      // If not a leaf, continue traversing
      if (!isLeaf) {
        currentLevel = currentLevel[part].children
      }
    })
  })

  // Convert map structure to array for Ant Design Tree
  const convertToArray = (map: TreeNodeMap): AttributeTreeNode[] => {
    return Object.keys(map)
      .sort() // Sort alphabetically by key
      .map((key) => {
        const item = map[key]
        const node = { ...item.node }

        if (Object.keys(item.children).length > 0) {
          node.children = convertToArray(item.children)
          node.isLeaf = false
        } else {
          node.isLeaf = true
          delete node.children // Remove empty children array for leaf nodes
        }

        return node
      })
  }

  return convertToArray(root)
}
